import close from '@assets/generalItems/deleteCrossGray.png'
import { useRoomForm } from '@hooks/useRoomForm'
import { useAppStore } from '@store'
import { ChangeEvent, Fragment, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

export function RoomForm() {
  const unselectAll = useAppStore.use.unselectAll()
  const selectedRoom = useAppStore.use.selectedRoom() ?? 'RECTA'
  const drawSquareRoom = useAppStore.use.drawSquareRoom()
  const deleteAllWalls = useAppStore.use.deleteAllWalls()
  const drawStraightRoom = useAppStore.use.drawStraightRoom()
  const drawLRoom = useAppStore.use.drawLRoom()
  const drawLRightRoom = useAppStore.use.drawLRightRoom()
  const drawLLeftRoom = useAppStore.use.drawLLeftRoom()
  const closeModal = useAppStore.use.closeModal()
  const navigate = useNavigate()
  const { action } = useParams()

  const { roomForm, setRoomForm, height, setHeight } = useRoomForm(selectedRoom)

  const thickness =
    roomForm && Object.keys(roomForm).length > 0 ? Object.values(roomForm)[0].thickness : 10

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Enter') {
        handleConfirm()
      }
    }
    document.body.addEventListener('keydown', handleKey)
    return () => {
      document.body.removeEventListener('keydown', handleKey)
    }
  }, [handleConfirm, height, roomForm, selectedRoom])

  function handleConfirm() {
    if (action === 'new') deleteAllWalls()
    unselectAll()

    const roomValues = Object.values(roomForm)

    switch (selectedRoom) {
      case 'CUADRADA':
        const [sizesA, sizesB] = roomValues
        drawSquareRoom(sizesA, sizesB, height, thickness)
        break
      case 'RECTA':
        const [sizeRecta] = roomValues
        drawStraightRoom(sizeRecta, height, thickness)
        break
      case 'ENL':
        const [size1, size2] = roomValues
        drawLRoom(size1, size2, height, thickness)
        break
      case 'LDERECHA':
        drawLRightRoom(...roomValues, height, thickness)
        break
      case 'LIZQUIERDA':
        drawLLeftRoom(...roomValues, height, thickness)
        break
      default:
        break
    }
    closeModal()
    navigate('/planner')
  }

  function handleClose() {
    closeModal()
    navigate('/planner')
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>, index: number) {
    setRoomForm((prev) => {
      const entries = Object.entries(prev || {})
      entries[index][1].width = parseInt(event.target.value)
      return Object.fromEntries(entries)
    })
  }

  function handleThicknessChange(event: ChangeEvent<HTMLInputElement>) {
    const newThickness = parseInt(event.target.value)

    setRoomForm((prev) => {
      if (!prev) return {}

      return Object.fromEntries(
        Object.entries(prev).map(([key, room]) => [key, { ...room, thickness: newThickness }]),
      )
    })
  }

  function handleHeightChange(event: ChangeEvent<HTMLInputElement>) {
    setHeight(parseInt(event.target.value))
  }

  return (
    <section>
      <div className='flex min-h-min flex-col'>
        <div className='right text-md sticky top-0 -ml-[0.5px] -mr-[0.75px] -mt-[0.5px] flex justify-between rounded-tr-lg bg-C/Color3 px-6 pb-[0.578em] pr-2 pt-[0.46em] font-medium text-C/Color1'>
          {'Medidas'}
          <img
            className='mr-1 mt-1 h-2 cursor-pointer'
            role='button'
            src={close}
            onClick={handleClose}
          />
        </div>
        <div className='scrollable flex h-[740px] w-full flex-col overflow-y-auto overflow-x-hidden'>
          <div className='px-4 py-8 font-medium text-C/Color2'>
            {'Para construir tu estancia, introduce las medidas de tu habitación'}
          </div>
          <div className='ml-8 flex flex-col content-center items-start gap-x-8 p-8'>
            <fieldset className='flex flex-col gap-y-8'>
              {roomForm &&
                Object.values(roomForm).map(({ name, width }, i) => (
                  <Fragment key={name}>
                    <div className='flex w-[44em] content-center justify-evenly rounded-lg bg-C/Color10 px-20 py-8 shadow-xl'>
                      <span className='text-C/Color4'>{'Medidas pared'}</span>
                      <label className='text-lg font-bold text-main-red'>{name}</label>
                      <span className='text-C/Color5'>{'Ancho'}</span>
                      <input
                        className='text-md block h-6 w-28 border border-solid border-C/Color4 px-1 text-right font-normal leading-3 text-C/Color4'
                        type='number'
                        name='width'
                        value={width}
                        onChange={(e) => handleInputChange(e, i)}
                      />
                    </div>
                  </Fragment>
                ))}
            </fieldset>

            <div className='flex w-[44em] content-center justify-start gap-x-[2.15rem] rounded-lg bg-C/Color10 px-6 py-8 shadow-xl'>
              <span className='ml-10 text-C/Color4'>{'Altura de las paredes'}</span>
              <label
                className='text-C/Color5'
                htmlFor='height'
              >
                {'Alto'}
              </label>
              <input
                className='text-md block h-6 w-28 border border-C/Color4 px-1 text-right font-normal leading-3 text-C/Color4'
                type='number'
                name='height'
                value={height}
                onChange={handleHeightChange}
              />

              <label
                className='text-C/Color5'
                htmlFor='thickness'
              >
                {'Grueso'}
              </label>
              <input
                className='text-md block h-6 w-28 border border-C/Color4 px-1 text-right font-normal leading-3 text-C/Color4'
                type='number'
                name='thickness'
                value={thickness}
                onChange={handleThicknessChange}
              />
            </div>
          </div>
          <div className='mb-6 ml-12 mr-14 mt-auto flex content-end gap-2 self-end'>
            <button
              className='cursor-pointer rounded-sm border-none bg-C/Color3 px-7 py-0.5 text-xs text-C/Color1'
              onClick={handleConfirm}
            >
              {'Ok'}
            </button>

            <button
              className='cursor-pointer rounded-sm border border-C/Color3 bg-C/Color1 px-4 py-0.5 text-xs text-C/Color3'
              onClick={handleClose}
            >
              {'Cancelar'}
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
